import React, { useEffect, useState } from 'react';
import { Loader2, GripVertical, ArrowUp, ArrowDown, EyeOff, Eye, Save } from 'lucide-react';
import { adminCollections, adminCollectionOrder, adminSaveCollectionOrder } from '../../services/cms';
import { useToast } from '../../hooks/use-toast';

const CollectionsOrderPanel = () => {
  const [collections, setCollections] = useState(null);
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const { toast } = useToast();

  useEffect(() => { adminCollections().then((c) => { setCollections(c); if (c[0]) setSlug(c[0].slug); }).catch(() => setCollections([])); }, []);

  useEffect(() => {
    if (!slug) return;
    setLoading(true); setItems(null);
    adminCollectionOrder(slug).then((d) => { setItems(d.items); setName(d.collection.name); }).catch(() => setItems([])).finally(() => setLoading(false));
  }, [slug]);

  const move = (from, to) => {
    if (to < 0 || to >= items.length) return;
    const next = [...items];
    const [it] = next.splice(from, 1);
    next.splice(to, 0, it);
    setItems(next);
  };

  const toggleExclude = (idx) => {
    const next = items.map((it, i) => i === idx ? { ...it, excluded: !it.excluded } : it);
    setItems(next);
  };

  const onDrop = (idx) => { if (dragIndex !== null && dragIndex !== idx) move(dragIndex, idx); setDragIndex(null); };

  const save = async () => {
    if (!window.confirm(`Enregistrer l’ordre de « ${name} » ? L’ordre de lecture public sera mis à jour (Citytoon.org non modifié).`)) return;
    let order = 0;
    const payload = items.map((it) => {
      if (it.excluded) return { source_id: it.source_id, order: null, excluded: true };
      order += 1;
      return { source_id: it.source_id, order, excluded: false };
    });
    await adminSaveCollectionOrder(slug, payload);
    // reflect numbering locally
    let o = 0;
    setItems(items.map((it) => it.excluded ? { ...it, order: null } : { ...it, order: ++o }));
    toast({ title: 'Ordre enregistré', description: `${payload.filter((p) => !p.excluded).length} webtoon(s) ordonnés.` });
  };

  const active = (items || []).filter((it) => !it.excluded);

  return (
    <div className="space-y-5" data-testid="collections-order-panel">
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 text-sm text-blue-800 font-600">Ordre éditorial local à Citytoon Engage. Glissez-déposez (ou flèches) pour ordonner les webtoons d’une collection. Les taxonomies Citytoon/Toocheke ne sont jamais modifiées.</div>

      <div className="flex flex-wrap items-center gap-3">
        <label className="text-xs font-800 uppercase text-muted-foreground">Collection</label>
        {collections === null ? <Loader2 className="w-5 h-5 animate-spin text-primary" /> : (
          <select value={slug} onChange={(e) => setSlug(e.target.value)} data-testid="order-collection-select" className="px-3 py-2 rounded-xl bg-muted font-700 text-sm outline-none cursor-pointer">
            {collections.map((c) => <option key={c.slug} value={c.slug}>{c.name} ({c.count})</option>)}
          </select>
        )}
        {items && <button onClick={save} data-testid="order-save-btn" className="ml-auto flex items-center gap-2 px-5 py-2 rounded-full bg-primary text-white font-800 text-sm hover:scale-[1.02] transition-transform"><Save className="w-4 h-4" /> Enregistrer l’ordre</button>}
      </div>

      {active.length > 0 && (
        <div className="bg-card border border-border rounded-2xl p-4 text-sm font-700 flex flex-wrap gap-x-6 gap-y-1">
          <span className="text-muted-foreground">Aperçu :</span>
          <span>Premier · <span className="text-primary">{active[0]?.titre}</span></span>
          {active.length > 1 && <span>Dernier · <span className="text-primary">{active[active.length - 1]?.titre}</span></span>}
        </div>
      )}

      {loading || items === null ? (
        <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <p className="text-muted-foreground font-600">Aucun webtoon validé dans cette collection.</p>
      ) : (
        <div className="space-y-2">
          {items.map((it, idx) => (
            <div key={it.source_id}
              draggable
              onDragStart={() => setDragIndex(idx)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(idx)}
              data-testid={`order-item-${it.slug}`}
              className={`flex items-center gap-3 bg-card border rounded-2xl p-3 transition-colors ${it.excluded ? 'border-dashed border-border opacity-60' : 'border-border'} ${dragIndex === idx ? 'ring-2 ring-primary' : ''}`}>
              <GripVertical className="w-5 h-5 text-muted-foreground cursor-grab shrink-0" />
              <span className="w-7 h-7 rounded-full bg-primary/10 text-primary text-xs font-800 flex items-center justify-center shrink-0">{it.excluded ? '—' : idx - items.slice(0, idx).filter((x) => x.excluded).length + 1}</span>
              {it.featured_image?.url ? <img src={it.featured_image.url} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" /> : <div className="w-12 h-12 rounded-lg bg-muted shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="font-display font-700 truncate">{it.titre}</p>
                <p className="text-xs text-muted-foreground font-600 truncate">{(it.auteurs || []).map((a) => a.name).join(', ') || 'Sans auteur'} · {it.images_count} image(s)</p>
              </div>
              <button onClick={() => move(idx, idx - 1)} className="w-8 h-8 rounded-full bg-muted flex items-center justify-center hover:bg-primary hover:text-white transition-colors" title="Monter"><ArrowUp className="w-4 h-4" /></button>
              <button onClick={() => move(idx, idx + 1)} className="w-8 h-8 rounded-full bg-muted flex items-center justify-center hover:bg-primary hover:text-white transition-colors" title="Descendre"><ArrowDown className="w-4 h-4" /></button>
              <button onClick={() => toggleExclude(idx)} data-testid={`order-exclude-${it.slug}`} className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${it.excluded ? 'bg-amber-500 text-white' : 'bg-muted hover:bg-amber-500 hover:text-white'}`} title={it.excluded ? 'Réintégrer' : 'Exclure de l’ordre'}>{it.excluded ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CollectionsOrderPanel;
